import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { trackUse } from '@/state/usage'
import { PreviewButton, canPreview } from '@/components/PreviewButton'
import { Link, useParams } from '@tanstack/react-router'
import { ChevronRight } from 'lucide-react'
import { api, mutate } from '@/runtime'
import { useAllPages, useMergeCards, useResource } from '@/db/hooks'
import { mergeViewFor, mergeWords } from '@/lib/mergeQueue'
import type { ReviewFile, ReviewPull } from '@/db/api'
import type { Resource } from '@/db/types'
import type { AgentRunResource } from '@/kinds/agentrun'
import { isOver } from '@/kinds/agentrun/schema'
import { byLayer } from '@/lib/layers'
import { findWalkthrough } from '@/lib/walkthrough'
import { MdLite } from '@/lib/mdlite'
import { DiffFile } from '@/components/DiffFile'
import { Skeleton } from '@/components/Skeleton'
import { RunShots } from '@/components/Shots'
import { Button, Empty, Failed, PageTitle, Tag, cx } from '@/components/ui'

// Reviewing a pull request inside DevOS (/dev/review/$id, the id of an agent
// session or a PullRequest mirror): the files grouped into layers, each with the
// agent's one-line reason when an agent made it, the diff, and the review.
// Asking for changes on an agent's PR also tells the agent, so it carries on.

interface Target {
  repo: string
  number: number
  run?: AgentRunResource
}

const targetOf = (r: Resource | undefined | null): Target | null => {
  if (!r) return null
  if (r.kind === 'AgentRun') {
    const run = r as AgentRunResource
    if (!run.spec.pr_number) return null
    return { repo: run.spec.repo_url.replace(/^https?:\/\/[^/]+\//, ''), number: run.spec.pr_number, run }
  }
  if (r.kind === 'PullRequest') {
    const spec = r.spec as { repo?: string; number?: number }
    if (spec.repo && spec.number) return { repo: spec.repo, number: spec.number }
  }
  return null
}

/** Two titles that say the same thing, give or take case, spacing and a closing full stop. */
const sameWords = (a?: string, b?: string) => {
  const n = (s?: string) =>
    (s ?? '')
      .toLowerCase()
      .replace(/\s+/g, ' ')
      .replace(/[.\s]+$/, '')
      .trim()
  return !!a && n(a) === n(b)
}

const errorText = (e: unknown) =>
  !navigator.onLine
    ? 'You are offline. Review needs a connection.'
    : e instanceof Error
      ? e.message
      : String(e)

const FileCard = ({ file, why, startOpen }: { file: ReviewFile; why?: string; startOpen: boolean }) => {
  const [open, setOpen] = useState(startOpen)
  return (
    <li className="card overflow-hidden" data-testid="review-file" data-path={file.path}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full text-left px-3 py-2.5 flex items-start gap-2 min-h-11"
      >
        <ChevronRight
          size={14}
          className={cx('mt-1 shrink-0 transition-transform', open && 'rotate-90')}
          aria-hidden
        />
        <span className="flex-1 min-w-0">
          <span className="block font-mono text-[13px] break-all">
            {file.old_path && file.status === 'renamed' ? `${file.old_path} → ` : ''}
            {file.path}
          </span>
          {why && <span className="block text-sm text-muted mt-0.5">{why}</span>}
        </span>
        <span className="shrink-0 text-xs font-mono">
          {file.status !== 'modified' && <Tag>{file.status}</Tag>}{' '}
          <span className="text-emerald-500">+{file.additions}</span>{' '}
          <span className="text-rose-400">−{file.deletions}</span>
        </span>
      </button>
      {open && (
        <div className="border-t border-line">
          <DiffFile file={file} />
        </div>
      )}
    </li>
  )
}

const askedKey = (repo: string, number: number) => `walkthrough-asked:${repo}#${number}`

const Walkthrough = ({ repo, number }: { repo: string; number: number }) => {
  const page = findWalkthrough(useAllPages(), repo, number)
  const [asked, setAsked] = useState(() => localStorage.getItem(askedKey(repo, number)) ?? '')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  // waiting: asked, and no page newer than the ask has arrived
  const waiting = !!asked && (!page || page.created_at < asked)
  const ask = async () => {
    setBusy(true)
    setError('')
    try {
      await api.reviewWalkthrough(repo, number)
      const at = new Date().toISOString()
      localStorage.setItem(askedKey(repo, number), at)
      setAsked(at)
    } catch (e) {
      setError(errorText(e))
    } finally {
      setBusy(false)
    }
  }
  const text = page ? String((page.spec as { text?: string }).text ?? '') : ''
  return (
    <section className="card px-4 py-3 mb-5" data-testid="review-walkthrough">
      <div className="flex items-center gap-2">
        <h2 className="font-semibold flex-1">Walkthrough</h2>
        <Button
          variant="chip"
          onClick={() => void ask()}
          disabled={busy || waiting}
          data-testid="review-walkthrough-ask"
        >
          {page ? 'Ask again' : 'Ask Prime for a walkthrough'}
        </Button>
      </div>
      {waiting && (
        <p className="text-sm text-muted mt-1" data-testid="review-walkthrough-waiting">
          Asked at {new Date(asked).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}. Prime is
          writing it; it shows here when it lands.
        </p>
      )}
      {error && <p className="text-sm text-muted mt-1">{error}</p>}
      {page && (
        <div className="mt-2" data-testid="review-walkthrough-page">
          <MdLite text={text} className="text-sm" />
          <Link to="/pages/$id" params={{ id: page.id }} className="text-sm text-accent-text underline">
            Open the page
          </Link>
        </div>
      )}
    </section>
  )
}

export const PRReviewView = () => {
  const { id } = useParams({ strict: false }) as { id: string }
  const resource = useResource(id) as Resource | undefined
  const target = useMemo(() => targetOf(resource), [resource])
  const [data, setData] = useState<ReviewPull | null>(null)
  const [error, setError] = useState('')
  const [asking, setAsking] = useState(false)
  const [text, setText] = useState('')
  const [busy, setBusy] = useState('')
  const [done, setDone] = useState('')

  const load = useCallback(() => {
    if (!target) return
    api
      .reviewPull(target.repo, target.number)
      .then((d) => {
        setData(d)
        setError('')
      })
      .catch((e: unknown) => setError(errorText(e)))
  }, [target])
  useEffect(() => {
    load()
  }, [load])
  // a card for this pull request in the merge queue (Chat's Do it): its place shows here too
  const queue = useMergeCards()
  const mv = target ? mergeViewFor(target.repo, target.number, queue) : { state: 'idle' as const }
  const queued = mv.state === 'queued' || mv.state === 'merging' || mv.state === 'updating'
  const wasQueued = useRef(false)
  useEffect(() => {
    // it left the queue (merged, or failed): read the pull request again
    if (wasQueued.current && !queued) load()
    wasQueued.current = queued
  }, [queued, load])

  const report = target?.run?.spec.report
  const whyOf = useMemo(() => new Map((report?.change ?? []).map((c) => [c.file, c.why])), [report])
  const groups = useMemo(() => byLayer(data?.files ?? []), [data])
  const totalLines = (data?.files ?? []).reduce((n, f) => n + f.additions + f.deletions, 0)

  if (resource === undefined) return null
  if (!target) return <Empty>Nothing to review here: no pull request.</Empty>

  const submit = async (kind: 'approve' | 'request_changes') => {
    setBusy(kind)
    setError('')
    try {
      await api.reviewSubmit(target.repo, target.number, kind, kind === 'request_changes' ? text.trim() : '')
      const run = target.run
      if (kind === 'request_changes' && run && !isOver(run.spec.status))
        await mutate.action(run.id, 'say', { text: `Review on PR #${target.number}: ${text.trim()}` })
      setDone(
        kind === 'approve' ? 'Approved.' : run ? 'Sent to Forgejo and to the agent.' : 'Sent to Forgejo.',
      )
      setAsking(false)
      setText('')
    } catch (e) {
      setError(errorText(e))
    } finally {
      setBusy('')
    }
  }
  const merge = async () => {
    setBusy('merge')
    setError('')
    try {
      await api.reviewMerge(target.repo, target.number, 'squash')
      trackUse('action:merge')
      setDone('Merged.')
      load()
    } catch (e) {
      setError(errorText(e))
    } finally {
      setBusy('')
    }
  }

  const pr = data?.pr
  const open = pr && pr.state === 'open' && !pr.merged
  return (
    <div className="lg:grid lg:grid-cols-[14rem_minmax(0,1fr)] lg:gap-8" data-testid="review">
      <aside className="hidden lg:block">
        <nav className="sticky top-3 flex flex-col gap-0.5" aria-label="Layers">
          {groups.map((g) => (
            <a
              key={g.layer.id}
              href={`#layer-${g.layer.id}`}
              className="rounded-[var(--radius-control)] px-3 py-2 hover:bg-raised flex items-center gap-2 text-sm"
            >
              <span className="flex-1">{g.layer.title}</span>
              <span className="text-[11px] text-faint">{g.files.length}</span>
            </a>
          ))}
        </nav>
      </aside>
      <div className="min-w-0 pb-40">
        <PageTitle>{pr ? pr.title : `PR #${target.number}`}</PageTitle>
        <div className="flex flex-wrap items-center gap-1.5 text-xs text-muted -mt-2 mb-4">
          <Tag tone={pr?.merged ? 'done' : pr?.state === 'closed' ? 'muted' : 'accent'}>
            #{target.number} {pr ? (pr.merged ? 'merged' : pr.state) : ''}
          </Tag>
          <span className="font-mono">{target.repo}</span>
          {pr && (
            <span className="font-mono text-faint">
              {pr.head} → {pr.base}
            </span>
          )}
          {pr?.url && (
            <a href={pr.url} target="_blank" rel="noreferrer" className="text-accent-text underline">
              Forgejo ↗
            </a>
          )}
        </div>
        {report && (report.cause || report.verify?.length || !sameWords(report.headline, pr?.title)) && (
          <section className="card px-4 py-3 mb-5" data-testid="review-summary">
            {/* the agent's headline, unless it is the page's title word for word */}
            {!sameWords(report.headline, pr?.title) && (
              <h2 className="font-semibold leading-snug">{report.headline}</h2>
            )}
            {report.cause && <p className="text-sm text-muted mt-1 leading-relaxed">{report.cause}</p>}
            {!!report.verify?.length && (
              <details className="mt-2">
                <summary className="cursor-pointer select-none text-[13px] font-semibold text-muted py-1">
                  How to check it
                </summary>
                <ul className="list-disc pl-5 text-sm flex flex-col gap-1">
                  {report.verify.map((v, i) => (
                    <li key={i}>{v}</li>
                  ))}
                </ul>
              </details>
            )}
          </section>
        )}
        {!!target.run?.spec.shots?.length && (
          <section className="card px-4 py-3 mb-5" data-testid="review-shots">
            <h2 className="font-semibold mb-3">Before and after</h2>
            <RunShots run={target.run} />
          </section>
        )}
        <Walkthrough repo={target.repo} number={target.number} />
        {error && <Failed what="the pull request's changes" reason={error} onRetry={load} className="mb-3" />}
        {!data && !error && (
          <div className="flex flex-col gap-3">
            <Skeleton className="h-6 w-1/3" />
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-16 w-full" />
          </div>
        )}
        {groups.map((g) => (
          <section
            key={g.layer.id}
            id={`layer-${g.layer.id}`}
            className="mb-6 scroll-mt-4"
            data-testid="review-layer"
          >
            <h2 className="text-[15px] font-semibold">{g.layer.title}</h2>
            {g.layer.blurb && <p className="text-xs text-faint mb-2">{g.layer.blurb}</p>}
            <ul className="flex flex-col gap-2">
              {g.files.map((f) => (
                <FileCard key={f.path} file={f} why={whyOf.get(f.path)} startOpen={totalLines <= 400} />
              ))}
            </ul>
          </section>
        ))}

        {open && (
          // on a phone the page under the bar fades out to the tab bar, so no diff shows through the gap
          <div
            className="md:hidden fixed inset-x-0 bottom-0 z-10 pointer-events-none bg-gradient-to-t from-ink from-60% to-transparent h-[calc(var(--above-tabbar)+9rem)]"
            aria-hidden
          />
        )}
        {open && (
          <div className="fixed md:sticky left-0 right-0 bottom-[calc(var(--above-tabbar)+0.5rem)] md:bottom-3 px-3 md:px-0 z-20">
            <div className="rounded-[var(--radius-composer)] bg-panel border border-line p-2 shadow-sm">
              {asking && (
                <textarea
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  placeholder={
                    target.run ? 'What should change? The agent gets this too.' : 'What should change?'
                  }
                  className="bare-input w-full min-h-20 px-2 py-1.5 text-[15px] resize-none"
                  autoFocus
                  data-testid="review-text"
                />
              )}
              {done && <p className="text-xs text-muted px-2 pb-1">{done}</p>}
              {mv.state !== 'idle' && mv.state !== 'merged' && (
                <p className="text-xs text-muted px-2 pb-1" role="status" data-testid="review-merge-state">
                  {mergeWords(mv)}
                </p>
              )}
              {/* a phone: the lesser actions share a row, the merge has the row under them;
                  a desk: one row, the merge last */}
              <div
                className={cx(
                  'grid gap-1.5 md:flex md:flex-wrap md:justify-end',
                  asking
                    ? 'grid-cols-2'
                    : target.run && canPreview(target.run)
                      ? 'grid-cols-3'
                      : 'grid-cols-2',
                )}
              >
                {asking ? (
                  <>
                    <Button variant="chip" onClick={() => setAsking(false)}>
                      Not now
                    </Button>
                    <Button
                      variant="chipOn"
                      onClick={() => void submit('request_changes')}
                      disabled={!text.trim() || !!busy}
                      data-testid="review-send-changes"
                    >
                      Send
                    </Button>
                  </>
                ) : (
                  <>
                    {target.run && <PreviewButton run={target.run} />}
                    <Button
                      variant="chip"
                      onClick={() => setAsking(true)}
                      data-testid="review-request-changes"
                    >
                      Request changes
                    </Button>
                    <Button
                      variant="chip"
                      onClick={() => void submit('approve')}
                      disabled={!!busy}
                      data-testid="review-approve"
                    >
                      Approve
                    </Button>
                    <Button
                      variant="primary"
                      className="col-span-full"
                      onClick={() => void merge()}
                      disabled={!!busy || queued}
                      data-testid="review-merge"
                    >
                      {busy === 'merge' || mv.state === 'merging'
                        ? 'Merging…'
                        : mv.state === 'queued' || mv.state === 'updating'
                          ? mergeWords(mv)
                          : 'Squash and merge'}
                    </Button>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
